import { OnsiteBlock, OnsiteActivity } from 'globals/store/onsite.data';
import { OnsiteView } from 'src/app/store/reselectors/onsiteBuilder.reselector';
import { N_TICKS } from 'src/app/helpers/constants';

export const MIN_TICK_HEIGHT = 50; // is also minimum brick height.  NO IDEA WHY THERE WERE TWO VARIABLES FOR THIS BEFORE
export const RATIO = 2.2
export const SESSION_SPACING = 5
export const ACTIVITY_HEIGHT = 21
export const ACTIVITY_SPACING = 8
export const MIN_SESSION_HEIGHT = 40
export const MAX_SESSION_HEIGHT = 60 * RATIO + SESSION_SPACING * 2 // before adding activities
export const STANDARD_ACTIVITY_HEIGHT = ACTIVITY_HEIGHT + ACTIVITY_SPACING
export const POPIN_ACTIVITY_HEIGHT = STANDARD_ACTIVITY_HEIGHT + 3

export const PROGRAM_TOP_SPACE = 180

export const initialise_view_control = (view: OnsiteView) => {
  view.shake = { row: -1, position: 1000000 }
  view.controls = view.lanes.map(lane => lane.type === 'program' ? [] : Array(lane.blocks.length).fill(0))
}

export interface Tick {
  height: number
  value: string
  top?: number
  start?: number
  secondaryValue?: string
}

export const initialise_ticks = (): Tick[] => 
  Array(N_TICKS).fill(null).map((_, t) => ({height: t === 24 ? 20 : MIN_TICK_HEIGHT, value: `${t < 10 ? '0' : ''}${t}:00` }))

const getMinMinute = (activityBlocks: OnsiteActivity[][], pointers: number[]) => Math.min(...activityBlocks.map((b, i) => b[pointers[i]] ? b[pointers[i]].justTime.m : 60 ))


const setActivityTopAndPositionAndReturnAddedActivitiesCountAtMinute = (activityBlocks, activityPointers, activityPosition, sessionTop, minute, timeMultiplier) => {
  let maxCount = 0
  activityBlocks.forEach((b,i) => {
    let tempMaxCount = 0
    let tempPosition = activityPosition
    while (true) {
      const activity = b[activityPointers[i]]
      if (!activity) break
      if (activity.justTime.m > minute) break
      const positionsToUse = tempPosition - activityPosition + 1
      // console.log({ positionsToUse, activity })
      const top = sessionTop + ACTIVITY_SPACING * positionsToUse + ACTIVITY_HEIGHT * (positionsToUse - 1) + activity.justTime.m/59 * timeMultiplier 
      // console.log({ top, sessionTop, positionsToUse })
      activity.top = top
      activity.position = tempPosition
      tempPosition++
      activityPointers[i]++
      tempMaxCount++
    }
    maxCount = tempMaxCount > maxCount ? tempMaxCount : maxCount
  })
  return maxCount
}

const addActivitiesToBlock = (activityBlocks: OnsiteActivity[][], activityPointers: number[], activityPosition = 1, cutoffMinute = 60, sessionTop = 0, currentMinute = 0, ignoreMinuteSpacing = true, timeMultiplier = 1, inSession = false) => {
  const oldActivityPostion = activityPosition
  const oldSessionTop = sessionTop
  while (true) {
    const minMinute = getMinMinute(activityBlocks, activityPointers)
    // console.log({ minMinute, sessionTop, cutoffMinute })
    if (minMinute >= cutoffMinute + (inSession ? 1 : 0)) {
      // console.log("Breaking...")
      break
    }
    if (!ignoreMinuteSpacing) {
      const minuteGap = minMinute - currentMinute
      currentMinute = currentMinute + minuteGap
      // console.log({ cutoffMinute, minuteGap, sessionTop })
      sessionTop += minuteGap*timeMultiplier
    }
    
    const added = setActivityTopAndPositionAndReturnAddedActivitiesCountAtMinute(activityBlocks, activityPointers, activityPosition, sessionTop, minMinute, timeMultiplier)
    if (!added) break
    sessionTop += ACTIVITY_SPACING * added + ACTIVITY_HEIGHT * added
    activityPosition += added
  }
  const addedCount = activityPosition - oldActivityPostion
  if (addedCount && !inSession) sessionTop += ACTIVITY_SPACING
  const addedHeight = sessionTop - oldSessionTop

  return { activityPosition, sessionTop, currentMinute, addedHeight, addedCount }
}


const modifyTickWithHeight = (top, height, tick) => {
  tick.top = top
  tick.height = height
  return top + height
}


const endsOnMinute = (session, hour) => session.endTime.h === hour + 1 && session.endTime.m === 0
const endsInALaterHour = (session, hour) => session.endTime.h > hour && !endsOnMinute(session, hour)

export const sizeAndPlaceActivitiesAndSessions = (view) => {
  if (!view.lanes || !view.lanes.length) return
  const firstLane = view.lanes[0]
  let top = 0
  // console.log({ view: jsonCopyObject(view) })

  switch (firstLane.type) {
    case "schedule": // Standalone schedules
      firstLane.blocks.forEach((block: OnsiteActivity[], blockIndex) => {
        const nActivities = block.length
        if(nActivities){
          block.forEach((activity, i) => {
            activity.top = i * STANDARD_ACTIVITY_HEIGHT + ACTIVITY_SPACING
            activity.position = i + 1
          })
        }
        const tickHeight = Math.max(MIN_TICK_HEIGHT, nActivities * STANDARD_ACTIVITY_HEIGHT + ACTIVITY_SPACING)
        const tick = view.ticks[blockIndex]
        tick.top = top
        tick.height = tickHeight
        top += tickHeight
      })
      break;
    default: // Program schedules
      let hour = 0
      let session: OnsiteBlock
      let previousSession: OnsiteBlock
      const activityLanes = view.lanes.slice(1, view.lanes.length)
      // console.log({ activityLanes })
      while (hour < 24) {
        firstLane.blocks[hour].map((b: OnsiteBlock) => b.containedActivities = 0) // set initial contained activity count.  Must be done here as overlapping hour sessions must be able to add activities more than once
        const sessionBlock: OnsiteBlock[] = [...firstLane.blocks[hour]]
        if (session) sessionBlock.unshift(session)
        
        const firstSession = sessionBlock[0]
        const startingMinute = firstSession ? (firstSession.startTime.h === hour ? firstSession.startTime.m : 0) : 0
        const lastSession = sessionBlock[sessionBlock.length - 1]
        const tick = view.ticks[hour]

        const activityBlocks: OnsiteActivity[][] = activityLanes.map(lane => lane.blocks[hour])
        const hasActivities = activityBlocks.some(b => !!b.length)
        const activityPointers: number[] = Array(activityBlocks.length).fill(0)

        if (hasActivities && !sessionBlock.length) {
          // activities in a block with no sessions are treated differently than activities elsewhere
          const toAdd2 = Math.max(
            MIN_TICK_HEIGHT, 
            addActivitiesToBlock(activityBlocks, activityPointers).sessionTop,
          )
          top = modifyTickWithHeight(top, toAdd2, tick)
          hour++
          continue
        }

        if (!hasActivities && !sessionBlock.length) { // just set the tick to be the minimum session height
          top = modifyTickWithHeight(top, MIN_TICK_HEIGHT, tick)
          hour++
          continue
        }

        // Starting and ending minutes are needed because spaces where there are sessions (or breaks!) are larger by a factor of RATIO than spaces where there are no sessions or breaks, discounting the effect of activities

        let sessionTop = 0 // session ? 0 : Math.min(startingActivityMinute, startingMinute)
        let currentMinute = 0
        let activityPosition = 1

        // Add activities before the sessions
        const beforeSessionsData = addActivitiesToBlock(activityBlocks, activityPointers, activityPosition, startingMinute, sessionTop, currentMinute, false)
        sessionTop = beforeSessionsData.sessionTop
        activityPosition = beforeSessionsData.activityPosition
        currentMinute = beforeSessionsData.currentMinute

        
        if (currentMinute < startingMinute) { // adjust for additional spacing before the session starts
          sessionTop += startingMinute - currentMinute
          currentMinute = startingMinute
        }

        // Add sessions and then activities within each session
        sessionBlock.forEach((s,i) => {
          let activityTop = sessionTop
          let inSessionSpacing = true
          const minute = s.startTime.m
          const outOfSession = s.startTime.h === hour && minute !== currentMinute
          if (outOfSession) {
            inSessionSpacing = !!(session || i)
            sessionTop += (minute - currentMinute) * (inSessionSpacing ? RATIO : 1) // if there was an overlapping session or this session is not the first in the list, expand the area by the ratio.  NOTE: This does NOT properly account for out of sessions that are the first in an hour.  MAY WANT TO COME BACK TO THIS.
          }

          const sessionCutoffMinute = s.endTime.h === hour ? s.endTime.m : 60
/* 

          (activityBlocks, activityPointers, activityPosition = 1, cutoffMinute = 60, sessionTop = 0, currentMinute = 0, ignoreMinuteSpacing = true, timeMultiplier = 1, inSession = false) */
          if (s.startTime.h === hour && minute > 0) { // only check for pre session activities if within the hour.  This will only have an effect when there is at least one session, and then an out of session in between, and then this session
            const outOfSessionData = addActivitiesToBlock(activityBlocks, activityPointers, activityPosition, minute - 1, activityTop, currentMinute, false, inSessionSpacing ? RATIO : 1, true)
            activityPosition = outOfSessionData.activityPosition
            if (outOfSessionData.addedCount) {
              sessionTop = outOfSessionData.sessionTop 
              
              activityPosition = outOfSessionData.activityPosition
              currentMinute = outOfSessionData.currentMinute
              sessionTop += (minute-currentMinute) * (inSessionSpacing ? RATIO : 1)
              activityTop = sessionTop
              currentMinute = minute
            }
          }

          let addedActivityHeight = 0
          const withinSessionData = addActivitiesToBlock(activityBlocks, activityPointers, activityPosition, sessionCutoffMinute, activityTop, currentMinute, false, inSessionSpacing ? RATIO : 1, true)
          activityPosition = withinSessionData.activityPosition
          if (withinSessionData.addedCount) {
            addedActivityHeight = (ACTIVITY_SPACING + ACTIVITY_HEIGHT) * withinSessionData.addedCount
          }
          s.containedActivities += withinSessionData.addedCount
          

          if (s.startTime.h < hour) { // overlap
            const addedHeight = SESSION_SPACING + addedActivityHeight + (s.endTime.h > hour ? MAX_SESSION_HEIGHT : s.endTime.m * RATIO)
            s.height += addedHeight + SESSION_SPACING // end of previous session, beginning of new
            sessionTop += addedHeight // only gets one SESSION_SPACING on top!
          } else { // new in hour
            let minHeight = MIN_SESSION_HEIGHT
            if (endsInALaterHour(s, hour)) {
              minHeight = s.endTime.h > hour + 1 ? 0 : MIN_SESSION_HEIGHT - 2 * SESSION_SPACING - s.endTime.m * RATIO // tack on all extra minimum height to this hour, even if it starts at :59 and stretches to [NextHour]:15.  This will be a little ugly, but is easier than the alternative of calculating it on both ends
            }
            const addedHeight = Math.max(minHeight, addedActivityHeight + ((s.endTime.h > hour ? 60 : s.endTime.m) - minute) * RATIO)
            sessionTop += SESSION_SPACING
            s.top = sessionTop
            s.height = addedHeight
            sessionTop += addedHeight 
          }

          currentMinute = s.endTime.h === hour ? s.endTime.m : 60
          
          if (previousSession && previousSession !== s) { // remember, sessions can overlap hours so this check could take place more than once.  That's why you need to check for prevSession !== s
            s.immediatePredecessorEndMinute = previousSession.endMinute
          }
          previousSession = s

        })
        // console.log({ sessionBlock})
        sessionTop += SESSION_SPACING

        // Add activities after the sessions
        if (currentMinute < 60) {
          const afterSessionsData = addActivitiesToBlock(activityBlocks, activityPointers, activityPosition, 60, sessionTop, currentMinute, false)
          sessionTop = afterSessionsData.sessionTop
          activityPosition = afterSessionsData.activityPosition
          currentMinute = afterSessionsData.currentMinute
          sessionTop += 60 - currentMinute
        }


        const toAdd = Math.max(sessionTop, MIN_TICK_HEIGHT)
        top = modifyTickWithHeight(top, toAdd, tick)
        session = endsInALaterHour(lastSession, hour) ? lastSession : null // new overlapping session.  Doesn't account for ending on 00 yet
        hour++
        
      }
      break;
  }
}

